import { useRef, useState, type DragEvent } from 'react';
import { Upload, FileText } from 'lucide-react';
import { cn } from '@/lib/cn';

type Props = {
  file: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  /** What the empty state says is accepted. Defaults to the general upload limit. */
  hint?: string;
};

export function FileDrop({ file, onChange, accept, hint = 'PDF, images, docs up to 25 MB' }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) onChange(dropped);
  }

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-8 text-center transition-colors',
        over && 'border-[var(--color-ring)] bg-[var(--color-surface-muted)]',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {file ? (
        <>
          <FileText className="size-6 text-[var(--color-foreground-muted)]" />
          <div className="text-sm font-medium">{file.name}</div>
          <div className="text-xs text-[var(--color-foreground-subtle)]">
            {(file.size / 1024).toFixed(1)} KB · click to replace
          </div>
        </>
      ) : (
        <>
          <Upload className="size-6 text-[var(--color-foreground-muted)]" />
          <div className="text-sm font-medium">Drop a file or click to choose</div>
          <div className="text-xs text-[var(--color-foreground-subtle)]">
            {hint}
          </div>
        </>
      )}
    </label>
  );
}
